/**
 * PropertyCard Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Displays individual property listing card with image, details, price, and CTA.
 * Used in property listing pages and search results. Includes hover animations and analytics.
 * 
 * Features:
 * - Property image gallery with slider (max 4 photos)
 * - Navigation arrows and dot indicators
 * - Smooth image transitions with fade effects
 * - Star rating display
 * - Bed/bath count with icons
 * - Guest capacity indicator
 * - Location display with map icon
 * - Price per night in brand color
 * - Hover animations (shadow, border color change)
 * - Analytics tracking on click
 * - Responsive design
 * - Badge support (e.g., "Featured", "New")
 * 
 * Gallery Features:
 * - Displays up to 4 images per property
 * - Automatic image carousel with manual controls
 * - Previous/Next navigation arrows (only shown if >1 image)
 * - Dot indicators for direct image navigation
 * - Smooth fade transitions between images
 * - Click-to-navigate functionality
 * - Hover effects on navigation controls
 * 
 * Props:
 * - property: PropertyData object containing:
 *   - id: string (unique identifier)
 *   - title: string (property name)
 *   - location: string (address/area)
 *   - price: number (EUR per night)
 *   - bedrooms: number
 *   - bathrooms: number
 *   - guests: number (max capacity)
 *   - images: string[] (image URLs)
 *   - badges: string[] (optional badges)
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - Framer Motion 12 (hover animations)
 * - React Router 7 (Link navigation)
 * - Font Awesome 6.5 (icons)
 * - DuotoneIcon (custom icon component)
 * - TailwindCSS 4.1 (styling)
 * - Analytics (trackSelectItem)
 * 
 * Styling:
 * - Hover shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1)
 * - Border color on hover: rgb(248 149 31) (brand-sun)
 * - Rounded corners: 2xl
 * - Transition duration: 300ms
 * 
 * Navigation:
 * - Links to /property/:id for detailed view
 * - Tracks analytics on selection
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with hover animations
 * - Fixed icon sizes (w-4 for valid Tailwind)
 * - Fixed CTA arrow color (text-white/90 for contrast)
 * - Integrated analytics tracking
 * - 2025-03-10: Added image gallery slider with navigation
 *   - Max 4 images per property with fade transitions
 *   - Previous/Next arrow navigation with hover effects
 *   - Dot indicators for direct image access
 *   - Responsive navigation controls
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProIcon } from './ProIcon';
import { DuotoneIcon, iconSchemes } from './DuotoneIcon';
import { trackSelectItem } from '../lib/analytics';
import { useSearchBar } from '../contexts/SearchBarContext';
import { isFavorite, toggleFavorite } from '../lib/favorites';

interface PropertyProps {
  property: {
    id: string;
    title: string;
    location: string;
    price: number;
    bedrooms: number;
    bathrooms: number;
    guests: number;
    sqm?: number;
    images: string[];
    badges: string[];
    rating?: number;
    reviewCount?: number;
  };
  showTotalPrice?: boolean;
}

export const PropertyCard: React.FC<PropertyProps> = ({ property, showTotalPrice = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { searchParams } = useSearchBar();
  const safeImages = Array.isArray(property.images) ? property.images.filter(Boolean) : [];
  const maxImages = Math.min(safeImages.length, 4);

  // Calculate nights and total price
  const hasDates = searchParams.dateRange[0] && searchParams.dateRange[1];
  const nights = hasDates 
    ? Math.ceil((searchParams.dateRange[1]!.getTime() - searchParams.dateRange[0]!.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const totalPrice = nights * property.price;

  // Load wishlist state from cookie on mount
  useEffect(() => {
    setIsWishlisted(isFavorite(property.id));
  }, [property.id]);

  useEffect(() => {
    if (maxImages === 0) {
      setCurrentImageIndex(0);
      return;
    }
    if (currentImageIndex >= maxImages) {
      setCurrentImageIndex(0);
    }
  }, [currentImageIndex, maxImages]);

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newState = toggleFavorite(property.id);
    setIsWishlisted(newState);
  };
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const displayImages = safeImages.slice(0, maxImages);

  // Swipe threshold in pixels
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (maxImages <= 1) return;
    if (touchStart === null || touchEnd === null) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setCurrentImageIndex((prev) => (prev + 1) % maxImages);
    }
    if (isRightSwipe) {
      setCurrentImageIndex((prev) => (prev - 1 + maxImages) % maxImages);
    }
  };

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (maxImages <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % maxImages);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (maxImages <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + maxImages) % maxImages);
  };
  return (
    <motion.div 
      whileHover={{ 
        y: -4
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="group ui-card overflow-hidden hover:border-slate-200 hover:shadow-xl transition-colors duration-300"
    >
      <Link
        to={`/property/${property.id}`}
        onClick={() =>
          trackSelectItem('rental_results', {
            id: property.id,
            name: property.title,
            price: property.price,
            category: property.badges[0],
          })
        }
        className="block"
      >
        {/* Image Gallery Slider */}
        <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
          {/* Images with hover zoom and swipe support */}
          <div 
            className="relative w-full h-full group touch-pan-y"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {maxImages > 0 ? (
              displayImages.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${property.title} - Image ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  } group-hover:scale-[1.02]`}
                  referrerPolicy="no-referrer"
                />
              ))
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-500 text-sm font-semibold">
                Image unavailable
              </div>
            )}
          </div>

          {/* Navigation Arrows - Only show if more than 1 image */}
          {maxImages > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all group"
                aria-label="Previous image"
              >
                <ProIcon 
                  icon="chevron-left" 
                  family="light"
                  className="w-3.5 h-3.5 text-slate-700 group-hover:text-brand-ocean transition-colors" 
                />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all group"
                aria-label="Next image"
              >
                <ProIcon 
                  icon="chevron-right" 
                  family="light"
                  className="w-3.5 h-3.5 text-slate-700 group-hover:text-brand-ocean transition-colors" 
                />
              </button>
            </>
          )}

          {/* Image Indicators */}
          {maxImages > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {displayImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentImageIndex
                      ? 'bg-white w-6'
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
          
          {/* Conversion Signal: Rare Find / Popular */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {parseInt(property.id) % 2 === 0 ? (
              <div className="bg-brand-ocean text-white ui-eyebrow px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5">
                <ProIcon icon="star" family="solid" className="w-3 h-3 text-amber-500" />
                Guest Favorite
              </div>
            ) : (
              <div className="bg-white/95 backdrop-blur-sm text-brand-ocean ui-eyebrow px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5 border border-slate-100">
                <ProIcon icon="family-dress" family="solid" className="w-3 h-3 text-brand-ocean" />
                Booking Fast
              </div>
            )}
          </div>

          <button 
            className={`absolute top-4 right-4 p-2.5 backdrop-blur-md rounded-xl transition-all shadow-lg z-10 ${
              isWishlisted 
                ? 'bg-[rgba(245,130,31,0.12)] text-[#F5821F]' 
                : 'bg-white/20 text-white hover:bg-white hover:text-[#F5821F]'
            }`}
            onClick={handleToggleWishlist}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            aria-pressed={isWishlisted}
          >
            <ProIcon 
              icon="heart" 
              family={isWishlisted ? 'solid' : 'light'}
              className={`w-5 h-5 ${isWishlisted ? 'text-brand-sun' : 'text-slate-600'}`}
            />
          </button>
          
          <div className="absolute bottom-4 left-4 flex gap-2">
            {property.badges.slice(0, 1).map((badge, i) => (
              <span key={i} className="bg-white/95 backdrop-blur-sm ui-eyebrow tracking-wider px-2.5 py-1.5 rounded-lg text-brand-ocean shadow-sm border border-slate-100">
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-start gap-2">
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-1.5 ui-eyebrow-brand tracking-[0.15em] sm:tracking-[0.2em]">
                <ProIcon icon="location-dot" family="light" className="w-3 h-3 text-brand-ocean flex-shrink-0" />
                <span className="truncate">{property.location}</span>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-brand-ocean line-clamp-1 uppercase tracking-tight">
                {property.title}
              </h3>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1">
              <div className="flex items-center gap-1.5 bg-brand-sun/10 border border-brand-sun/20 rounded-xl px-3 py-1.5">
                <ProIcon icon="star" family="solid" className="text-base text-brand-sun" />
                <span className="font-bold text-slate-900">{property.rating || 4.9}</span>
              </div>
              <span className="text-xs text-slate-500">{property.reviewCount || 127} reviews</span>
            </div>
          </div>

          <ul className="flex items-center gap-3 sm:gap-5 text-brand-gray" aria-label="Property details">
            <li className="flex items-center gap-1.5 sm:gap-2">
              <ProIcon icon="bed" family="sharp" className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-brand-ocean/40 flex-shrink-0" />
              <span className="text-sm font-bold text-brand-ocean/70">{property.bedrooms} BR</span>
            </li>
            <li className="flex items-center gap-1.5 sm:gap-2">
              <ProIcon icon="bath" family="sharp" className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-brand-ocean/40 flex-shrink-0" />
              <span className="text-sm font-bold text-brand-ocean/70">{property.bathrooms} BA</span>
            </li>
            <li className="flex items-center gap-1.5 sm:gap-2">
              <ProIcon icon="family-dress" family="solid" className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-brand-ocean/40 flex-shrink-0" />
              <span className="text-sm font-bold text-brand-ocean/70">{property.guests}<span className="hidden sm:inline"> Guests</span></span>
            </li>
            {property.sqm && (
              <li className="flex items-center gap-1.5 sm:gap-2">
                <span className="w-3.5 sm:w-4 h-3.5 sm:h-4 flex items-center justify-center text-brand-ocean/40 text-xs font-bold">m²</span>
                <span className="text-sm font-bold text-brand-ocean/70">{property.sqm} m²</span>
              </li>
            )}
          </ul>

          <div className="pt-4 border-t border-slate-100 flex justify-between items-center gap-3">
            <div className="min-w-0">
              {showTotalPrice && hasDates ? (
                <div className="space-y-1">
                  <p className="text-xl sm:text-2xl font-black text-brand-ocean" aria-label={`€${totalPrice} total for ${nights} nights`}>
                    €{totalPrice.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-500">
                    {nights} nights • €{property.price}/night
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-xl sm:text-2xl font-black text-brand-ocean" aria-label={`€${property.price} per night`}>
                    €{property.price}
                  </p>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <ProIcon icon="calendar" family="light" className="w-3 h-3" />
                    per night
                  </p>
                </div>
              )}
            </div>
            <button 
              className="group ui-btn-secondary p-2.5 sm:p-3 rounded-xl flex items-center gap-1.5 flex-shrink-0"
              aria-label={`Check details for ${property.title}`}
            >
              <span className="text-sm font-semibold text-white group-hover:text-white/90">View</span>
              <ProIcon icon="chevron-right" family="light" className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-white group-hover:text-white/90 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
